import React, { useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { useHistory } from "react-router-dom";
import {
    Card,
    CardContent,
    Typography,
    Chip,
    LinearProgress,
    Box,
    Avatar,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    makeStyles
} from "@material-ui/core";
import {
    MoreVert as MoreVertIcon,
    Chat as ChatIcon,
    History as HistoryIcon,
    Edit as EditIcon,
    Event as EventIcon,
    AcUnit,
    Brightness5,
    Whatshot
} from "@material-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import OpportunityHistoryModal from "../OpportunityHistoryModal";
import OpportunityModal from "../OpportunityModal";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
    card: {
        marginBottom: theme.spacing(0.75),
        cursor: "grab",
        transition: "all 0.2s ease",
        position: "relative",
        "&:hover": {
            boxShadow: theme.shadows[4],
            transform: "translateY(-2px)"
        }
    },
    cardDragging: {
        boxShadow: theme.shadows[8],
        transform: "rotate(2deg)",
        opacity: 0.9
    },
    menuButton: {
        position: "absolute",
        top: 4,
        right: 4,
        padding: 4
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
        fontSize: "0.85rem",
        paddingRight: theme.spacing(3)
    },
    date: {
        color: theme.palette.text.secondary,
        fontSize: "0.7rem",
        marginBottom: theme.spacing(0.75)
    },
    contact: {
        fontSize: "0.75rem",
        marginBottom: theme.spacing(0.75),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.5)
    },
    chip: {
        height: 20,
        fontSize: "0.7rem",
        fontWeight: 500,
        marginBottom: theme.spacing(0.75)
    },
    progressContainer: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.5)
    },
    progressBar: {
        height: 5,
        borderRadius: 3,
        backgroundColor: theme.palette.grey[200]
    },
    progressLabel: {
        fontSize: "0.65rem",
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5)
    },
    value: {
        fontWeight: 600,
        color: theme.palette.primary.main,
        fontSize: "0.85rem",
        marginTop: theme.spacing(0.75)
    },
    avatar: {
        width: 20,
        height: 20,
        fontSize: "0.7rem",
        backgroundColor: theme.palette.secondary.main
    },
    footer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: theme.spacing(1)
    },
    indicators: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.5)
    },
    temperatureIcon: {
        fontSize: 18
    },
    scheduleIcon: {
        fontSize: 18,
        color: theme.palette.primary.main
    }
}));

const OpportunityCard = ({ opportunity, index, onUpdate }) => {
    const classes = useStyles();
    const history = useHistory();
    const [anchorEl, setAnchorEl] = useState(null);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const handleMenuClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        setEditModalOpen(true);
    };

    const handleCloseEdit = () => {
        setEditModalOpen(false);
        if (onUpdate) onUpdate();
    };

    const handleGoToChat = async () => {
        handleMenuClose();
        if (opportunity.contactId) {
            try {
                // Buscar tickets do contato
                const { data } = await api.get(`/tickets?contactId=${opportunity.contactId}&showAll=true`);

                if (data.tickets && data.tickets.length > 0) {
                    // Pegar o ticket mais recente
                    const latestTicket = data.tickets[0];
                    history.push(`/tickets/${latestTicket.id}`);
                } else {
                    // Se não houver ticket, ir para página de tickets com filtro
                    history.push(`/tickets?contactId=${opportunity.contactId}`);
                }
            } catch (err) {
                console.error("Erro ao buscar ticket:", err);
                // Fallback: ir para página de tickets com filtro
                history.push(`/tickets?contactId=${opportunity.contactId}`);
            }
        }
    };

    const handleOpenHistory = () => {
        handleMenuClose();
        setHistoryModalOpen(true);
    };

    const formatDate = (date) => {
        if (!date) return "";
        try {
            return format(new Date(date), "dd MMM, yyyy", { locale: ptBR });
        } catch {
            return "";
        }
    };

    const formatValue = (value) => {
        if (!value) return null;
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL"
        }).format(value);
    };

    const getProgressColor = (percentage) => {
        if (percentage >= 76) return "#4caf50"; // verde
        if (percentage >= 51) return "#2196f3"; // azul
        if (percentage >= 26) return "#ff9800"; // laranja
        return "#f44336"; // vermelho
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const parts = name.split(" ");
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const getTemperatureIcon = (temperature) => {
        switch (temperature) {
            case "cold":
                return <AcUnit className={classes.temperatureIcon} style={{ color: "#2196f3" }} titleAccess="Lead Frio" />;
            case "warm":
                return <Brightness5 className={classes.temperatureIcon} style={{ color: "#ff9800" }} titleAccess="Lead Morno" />;
            case "hot":
                return <Whatshot className={classes.temperatureIcon} style={{ color: "#f44336" }} titleAccess="Lead Quente" />;
            default:
                return null;
        }
    };

    // Percentual: 100% para conversão/perda, senão usa o do card
    const cardType = opportunity.card?.type;
    const percentage = (cardType === "conversao" || cardType === "perda")
        ? 100
        : (opportunity.card?.percentage || 0);
    const cardColor = opportunity.card?.color || "#3498db";

    return (
        <>
            <OpportunityHistoryModal
                open={historyModalOpen}
                onClose={() => setHistoryModalOpen(false)}
                opportunity={opportunity}
            />

            <OpportunityModal
                open={editModalOpen}
                onClose={handleCloseEdit}
                funnelId={opportunity.funnelId}
                opportunityId={opportunity.id}
                reload={onUpdate}
            />

            <Draggable draggableId={String(opportunity.id)} index={index}>
                {(provided, snapshot) => (
                    <Card
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`${classes.card} ${snapshot.isDragging ? classes.cardDragging : ""}`}
                        elevation={snapshot.isDragging ? 8 : 1}
                    >
                        <CardContent>
                            <IconButton
                                size="small"
                                className={classes.menuButton}
                                onClick={handleMenuClick}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>

                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={handleEdit}>
                                    <ListItemIcon>
                                        <EditIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Editar" />
                                </MenuItem>
                                <MenuItem onClick={handleGoToChat} disabled={!opportunity.contactId}>
                                    <ListItemIcon>
                                        <ChatIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Ir para a Conversa" />
                                </MenuItem>
                                <MenuItem onClick={handleOpenHistory}>
                                    <ListItemIcon>
                                        <HistoryIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Históricos" />
                                </MenuItem>
                            </Menu>

                            <Typography className={classes.title}>
                                {opportunity.title}
                            </Typography>

                            <Typography className={classes.date}>
                                📅 {formatDate(opportunity.createdAt)}
                            </Typography>

                            {opportunity.contact && (
                                <Typography className={classes.contact}>
                                    👤 {opportunity.contact.name}
                                </Typography>
                            )}

                            <Chip
                                label={opportunity.card?.name || "Sem estágio"}
                                className={classes.chip}
                                size="small"
                                style={{
                                    backgroundColor: `${cardColor}20`,
                                    color: cardColor,
                                    border: `1px solid ${cardColor}40`
                                }}
                            />

                            <Box className={classes.progressContainer}>
                                <LinearProgress
                                    variant="determinate"
                                    value={percentage}
                                    className={classes.progressBar}
                                    style={{
                                        "& .MuiLinearProgress-bar": {
                                            backgroundColor: getProgressColor(percentage)
                                        }
                                    }}
                                    sx={{
                                        "& .MuiLinearProgress-bar": {
                                            backgroundColor: getProgressColor(percentage)
                                        }
                                    }}
                                />
                                <Typography className={classes.progressLabel}>
                                    {percentage}% concluído
                                </Typography>
                            </Box>

                            <Box className={classes.footer}>
                                {opportunity.value && (
                                    <Typography className={classes.value}>
                                        {formatValue(opportunity.value)}
                                    </Typography>
                                )}
                                <Box className={classes.indicators}>
                                    {opportunity.lastTemperature && getTemperatureIcon(opportunity.lastTemperature)}
                                    {opportunity.hasSchedule && (
                                        <EventIcon className={classes.scheduleIcon} titleAccess="Tem agendamento pendente" />
                                    )}
                                    {opportunity.contact && (
                                        <Avatar className={classes.avatar}>
                                            {getInitials(opportunity.contact.name)}
                                        </Avatar>
                                    )}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </Draggable>
        </>
    );
};

export default OpportunityCard;
